import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext(null);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

const translations = {
  en: {
    // Navbar
    nav: {
      logo: 'AdvokatTop',
      home: 'Home',
      attorneys: 'Attorneys',
      articles: 'Articles',
      findAttorney: 'Find Attorney',
      login: 'Sign In',
      register: 'Register',
      dashboard: 'Dashboard',
      submitCase: 'Submit Case',
      getAdvice: 'Get Advice',
      browseCases: 'Browse Cases',
      subscription: 'Subscription',
      logout: 'Logout',
      lawyersByLocation: 'Lawyers by Location',
      lawyersByArea: 'Lawyers by Practice Area',
      legalTopics: 'Legal Topics & Q&A',
    },
    regions: {
      tashkent_city: 'Tashkent (City)',
      tashkent: 'Tashkent Region',
      andijan: 'Andijan',
      fergana: 'Fergana',
      namangan: 'Namangan',
      samarkand: 'Samarkand',
      bukhara: 'Bukhara',
      navoi: 'Navoi',
      kashkadarya: 'Kashkadarya',
      surkhandarya: 'Surkhandarya',
      jizzakh: 'Jizzakh',
      sirdarya: 'Sirdarya',
      khorezm: 'Khorezm',
      karakalpakstan: 'Republic of Karakalpakstan',
    },
    // Landing Page
    landing: {
      title: 'Find the Right Legal Help',
      subtitle: 'Connect with experienced attorneys or offer your legal expertise',
      heroTitle: 'Legal problem? Find the right attorney in minutes',
      heroSubtitle: 'Browse verified attorneys, submit your case, or get quick legal advice',
      searchPlaceholder: 'Describe your issue or enter a practice area...',
      searchButton: 'Find an Attorney',
      amAttorneyBtn: "I'm an Attorney",
      needHelp: 'I Need Legal Help',
      needHelpDesc: 'Find qualified attorneys, submit your case, or get legal advice',
      amAttorney: 'I Am an Attorney',
      amAttorneyDesc: 'Connect with clients, grow your practice, and help those in need',
      howItWorks: 'How It Works',
      forClients: 'For Clients',
      forAttorneys: 'For Attorneys',
      step1Title: 'Describe Your Problem',
      step1Desc: 'Tell us briefly about your legal situation — no personal data required',
      step2Title: 'Choose an Attorney',
      step2Desc: 'Browse profiles of verified attorneys and view their practice areas',
      step3Title: 'Get Legal Help',
      step3Desc: 'Connect with your chosen attorney and resolve your legal matter',
      step1Client: 'Create an account and describe your legal needs',
      step2Client: 'Browse attorneys or submit your case for review',
      step3Client: 'Connect with attorneys and get the help you need',
      step1Attorney: 'Create your professional profile',
      step2Attorney: 'Subscribe to access client cases and contact info',
      step3Attorney: 'Respond to cases and grow your client base',
      statsAttorneys: 'Attorneys',
      statsCases: 'Cases Resolved',
      statsResponse: 'Avg. Response',
      statsResponseValue: '< 24h',
      quickFilters: 'Popular:',
    },
    // Login/Register
    auth: {
      loginTitle: 'Welcome Back',
      loginSubtitle: 'Sign in to your account',
      registerTitle: 'Create Account',
      registerSubtitle: 'Join our legal platform',
      email: 'Email Address',
      emailOrPhone: 'Email or phone',
      password: 'Password',
      confirmPassword: 'Confirm Password',
      fullName: 'Full Name',
      phone: 'Phone Number',
      loginButton: 'Sign In',
      registerButton: 'Create Account',
      noAccount: "Don't have an account?",
      haveAccount: 'Already have an account?',
      signUp: 'Sign up',
      signIn: 'Sign in',
      orContinueWith: 'Or continue with',
      iAmClient: 'I need legal help (Client)',
      iAmAttorney: 'I am an attorney',
      selectUserType: 'I am a...',
      barNumber: 'Bar Number',
      location: 'Location',
      practiceAreas: 'Practice Areas',
      bio: 'Professional Bio',
      hourlyRate: 'Hourly Rate ($)',
    },
    // Dashboard
    dashboard: {
      welcome: 'Welcome',
      attorney: 'Attorney',
      client: 'Client',
      manageProfile: 'Manage your practice and connect with clients',
      clientSubtitle: 'Manage your cases and find legal help',
      subscriptionActive: 'Subscription Active',
      subscriptionRequired: 'Subscription Required',
      planExpires: 'Your {plan} plan expires on {date}',
      subscribeToAccess: 'Subscribe to access full case details and client contact information',
      subscribeNow: 'Subscribe Now',
      openCasesAvailable: 'Open Cases Available',
      yourHourlyRate: 'Your Hourly Rate',
      practiceAreasCount: 'Practice Areas',
      yourProfile: 'Your Profile',
      barNumber: 'Bar Number',
      location: 'Location',
      phoneLabel: 'Phone',
      hourlyRateLabel: 'Hourly Rate',
      notSet: 'Not set',
      myCases: 'My Cases (Responded)',
      noRespondedCases: "You haven't responded to any cases yet.",
      browseAvailableCases: 'Browse available cases',
      recentOpenCases: 'Recent Open Cases',
      noOpenCases: 'No open cases available at the moment.',
      viewAllCases: 'View all cases',
      subscribeToViewDetails: 'Subscribe to view details',
      myCasesClient: 'My Cases',
      noCasesYet: "You haven't submitted any cases yet.",
      submitYourFirst: 'Submit your first case',
      adviceRequests: 'Advice Requests',
      noAdviceRequests: 'No advice requests yet.',
      requestAdvice: 'Request legal advice',
    },
    // Cases
    cases: {
      title: 'Browse Cases',
      subtitle: 'Find clients seeking legal representation',
      subscribeBanner: 'Subscribe to view full case details and client contact information',
      practiceArea: 'Practice Area',
      allAreas: 'All Areas',
      status: 'Status',
      allStatuses: 'All Statuses',
      open: 'Open',
      inProgress: 'In Progress',
      closed: 'Closed',
      loading: 'Loading cases...',
      noCasesFound: 'No cases found',
      adjustFilters: 'Try adjusting your filters',
      posted: 'Posted',
      client: 'Client',
      subscribeToView: 'Subscribe to view case description and client details',
      loginToView: 'Log in as a subscribed attorney to view details',
      backToCases: 'Back to Cases',
      backToDashboard: 'Back to Dashboard',
      description: 'Description',
      clientInformation: 'Client Information',
      name: 'Name',
      emailLabel: 'Email',
      phoneLabel: 'Phone',
      locationLabel: 'Location',
      subscriptionRequiredTitle: 'Subscription Required',
      subscriptionRequiredDesc: 'Subscribe to view the full case description and client contact information.',
      responses: 'Responses',
      noResponsesYet: 'No responses yet.',
      sendResponse: 'Send a Response',
      writeProposal: 'Write your proposal or response to this case...',
      submitResponse: 'Submit Response',
      submitting: 'Submitting...',
      responseSuccess: 'Response submitted successfully',
      responseUpdated: 'Response updated successfully',
      editResponse: 'Edit Your Response',
      updateResponse: 'Update Response',
      subscribeToRespond: 'Subscribe to respond to this case.',
    },
    practiceAreas: {
      'Family Law': 'Family Law',
      'Criminal Defense': 'Criminal Defense',
      'Personal Injury': 'Personal Injury',
      'Business Law': 'Business Law',
      'Real Estate': 'Real Estate',
      'Immigration': 'Immigration',
      'Bankruptcy': 'Bankruptcy',
      'Employment Law': 'Employment Law',
      'Estate Planning': 'Estate Planning',
      'Intellectual Property': 'Intellectual Property',
      'Tax Law': 'Tax Law',
      'Civil Rights': 'Civil Rights',
      'Other': 'Other',
    },
    statuses: {
      open: 'Open',
      in_progress: 'In Progress',
      closed: 'Closed',
      paid: 'Paid',
      pending: 'Pending',
    },
    clientDashboard: {
      findAttorneyTitle: 'Find an Attorney',
      findAttorneyDesc: 'Browse and search for qualified attorneys in your area',
      submitCaseTitle: 'Submit a Case',
      submitCaseDesc: 'Post your legal matter and receive proposals from attorneys',
      getLegalAdviceTitle: 'Get Legal Advice',
      getLegalAdviceDesc: 'Request professional legal advice on specific questions',
      yourProfile: 'Your Profile',
      yourCases: 'Your Cases',
      adviceRequests: 'Advice Requests',
      phone: 'Phone',
      location: 'Location',
      responses: 'responses',
    },
    register: {
      iNeedHelp: 'I Need Help',
      imAnAttorney: "I'm an Attorney",
      emailRequired: 'Email *',
      passwordRequired: 'Password *',
      forAttorneys: 'For Attorneys',
      confirmPasswordRequired: 'Confirm Password *',
      fullNameRequired: 'Full Name *',
      lastName: 'Last Name *',
      firstName: 'First Name *',
      middleName: 'Middle Name *',
      phone: 'Phone',
      location: 'Location',
      locationPlaceholder: 'City, State',
      barNumberRequired: 'Bar Number *',
      licenseDate: 'License Issue Date',
      yearsOfExperience: 'Years of Experience',
      hourlyRate: 'Hourly Rate ($)',
      practiceAreas: 'Practice Areas',
      bio: 'Bio',
      bioPlaceholder: 'Tell potential clients about your experience...',
      creatingAccount: 'Creating Account...',
      passwordsMismatch: 'Passwords do not match',
      passwordTooShort: 'Password must be at least 6 characters',
      phoneInvalid: 'Please enter your full phone number',
    },
    // Submit Case
    submitCase: {
      title: 'Submit Your Case',
      subtitle: 'Describe your legal needs and connect with qualified attorneys',
      caseTitle: 'Case Title',
      caseTitlePlaceholder: 'Brief title describing your legal matter',
      practiceArea: 'Practice Area',
      selectArea: 'Select practice area',
      caseDescription: 'Case Description',
      caseDescriptionPlaceholder: 'Please provide details about your legal situation. The more information you provide, the better attorneys can understand your needs.',
      submitButton: 'Submit Case',
      submittingButton: 'Submitting...',
      successMessage: 'Your case has been submitted successfully! Attorneys will be able to review and respond.',
      goToDashboard: 'Go to Dashboard',
    },
    // Request Advice
    requestAdvice: {
      title: 'Request Legal Advice',
      subtitle: 'Get professional legal guidance from qualified attorneys',
      subject: 'Subject',
      subjectPlaceholder: 'Brief subject of your legal question',
      description: 'Your Question',
      descriptionPlaceholder: 'Please describe your legal question in detail...',
      paymentTitle: 'Payment Information',
      amount: 'Advice Request Fee',
      paymentNote: 'This is a mock payment for demonstration purposes.',
      cardNumber: 'Card Number',
      expiryDate: 'Expiry Date',
      cvv: 'CVV',
      submitPayment: 'Submit Payment',
      processing: 'Processing...',
      continueToPayment: 'Continue to Payment',
      completePayment: 'Complete Payment',
      amountLabel: 'Amount:',
      mockPaymentInfo: 'This is a mock payment. Click "Pay Now" to simulate a successful payment.',
      payNow: 'Pay Now (Mock)',
      paidService: 'Paid service:',
      paidServiceDesc: 'This is a premium feature. Your request will be sent directly to our legal team for review. Payment is required to complete the submission.',
      feeLabel: 'One-time fee for legal advice request',
      questionHint: 'Be as specific as possible',
      successTitle: 'Request Submitted Successfully!',
      successDetails: 'Your advice request has been sent. An email notification has been delivered.',
      redirecting: 'Redirecting to dashboard...',
      successMessage: 'Your advice request has been submitted successfully! An attorney will respond to your email shortly.',
      headline: 'Get Legal Advice',
      headlineSub: 'Get a response from an experienced attorney quickly',
      fullName: 'Full Name',
      phone: 'Phone',
      questionLabel: 'What do you need advice on?',
      questionPlaceholder: 'Please describe your problem in detail...',
      priceLabel: 'Consultation fee',
      payBtn: 'Proceed to payment',
      preparing: 'Preparing...',
      registerAndPay: 'Register and pay',
      choosePayment: 'Choose payment method',
      cancel: 'Cancel',
      securePayment: 'Payment is made via a secure encrypted channel',
      scanQr: 'Scan the QR code in the Payme app',
      autoDetect: 'Payment is detected automatically...',
    },
    // Find Attorney
    findAttorney: {
      title: 'Find an Attorney',
      subtitle: 'Browse our directory of qualified legal professionals',
      search: 'Search',
      searchPlaceholder: 'Search by name...',
      searchPlaceholderDetailed: 'Name or keyword...',
      practiceArea: 'Practice Area',
      allAreas: 'All Practice Areas',
      locationLabel: 'Location',
      locationPlaceholder: 'City or state...',
      searchButton: 'Search',
      loading: 'Loading attorneys...',
      noAttorneysFound: 'No attorneys found',
      adjustSearch: 'Try adjusting your search criteria',
      perHour: '/hour',
      viewProfile: 'View Profile',
      noLocationSpecified: 'Location not specified',
      more: 'more',
      experienceLabel: 'Experience',
      months: 'mo.',
      years: 'yr.',
      viewProfileBtn: 'Details',
      callBtn: 'Call',
      profileBtn: 'Profile',
      messageBtn: 'Message',
      ratingLabel: 'Rating',
      ratingScore: 'pts',
      ratingReviews: 'reviews',
      ratingInfoTitle: 'About Rating',
      ratingInfoBody: 'Attorney rating is a 1-5 star average based on ratings given by registered clients on this platform. Each client can rate an attorney once, choosing 1 to 5 stars.',
      ratingDisclaimer: 'Disclaimer',
      ratingDisclaimerText: 'Users rate attorneys based on the quality of service they received. Therefore, attorney ratings depend on subjective factors. We do not recommend choosing an attorney based solely on their rating. The AdvokatTop platform does not participate in forming attorney ratings and does not guarantee that any rating reflects the attorney\'s qualifications.',
    },
    // Attorney Profile
    attorneyProfile: {
      backToDirectory: 'Back to Directory',
      barNumber: 'Bar License',
      practiceAreas: 'Practice Areas',
      about: 'About',
      interestedInWorking: 'Interested in working with {name}?',
      submitYourCase: 'Submit Your Case',
      requestAdviceBtn: 'Request Advice',
      loginOrRegister: 'Log in or register as a client to contact this attorney.',
      selectForCase: 'Select {name} for Your Case',
      selectDescription: 'You are about to select this attorney to handle your case. The attorney will be notified and the case will be marked as assigned.',
      selectButton: 'Select this Attorney',
      selecting: 'Selecting...',
      selectSuccess: 'Attorney selected successfully! Redirecting to your dashboard...',
    },
    contact: {
      loading: 'Loading...',
      notFound: 'Attorney not found',
      sentTitle: 'Message sent!',
      sentBody: 'Your message has been sent to {name}. You will receive a reply soon.',
      backHome: 'Back to home',
      title: 'Message',
      requiredNote: 'Fields marked with an asterisk (*) are required.',
      salutation: 'Dear {name},',
      situationLabel: 'Describe your situation *',
      situationPlaceholder: 'Briefly describe your legal situation or question...',
      phonePreferredLabel: 'I prefer the attorney to reply by phone (optional)',
      phoneLabel: 'Phone number *',
      preferredTimeLabel: 'Convenient time to call?',
      morning: 'Morning',
      afternoon: 'Afternoon',
      evening: 'Evening',
      sending: 'Sending...',
      sendBtn: 'Send message',
      registerAndSendBtn: 'Register and send',
      registerNote: 'Registration is required to send. Your data will be saved.',
    },
    messages: {
      loading: 'Loading...',
      myMessagesTitle: 'My messages',
      inboxTitle: 'Inbox',
      newSuffix: 'new',
      noSentMessages: "You haven't sent any messages yet",
      noInboxMessages: 'You have no messages yet',
      findAttorneyBtn: 'Find an attorney',
      toLabel: 'To:',
      newReplyBadge: 'New reply',
      anonymous: 'Anonymous',
      phonePreferredClient: 'Phone call preferred',
      phonePreferredAttorney: 'Prefers a phone reply',
      you: 'You',
      writeReplyPlaceholder: 'Write a reply...',
      sendShortcutHint: 'Ctrl+Enter to send',
      sending: 'Sending...',
      replyBtn: 'Reply',
      loadingThread: 'Loading conversation...',
    },
    // Subscription
    subscription: {
      title: 'Subscription Plans',
      subtitle: 'Get full access to cases and connect with clients',
      currentPlan: 'Current Plan',
      currentSubscriptionActive: 'Current Subscription Active',
      yourPlanActiveUntil: 'Your {plan} plan is active until',
      active: 'Active',
      expired: 'Expired',
      expiresOn: 'Expires on',
      choosePlan: 'Choose Your Plan',
      monthly: 'Monthly',
      yearly: 'Yearly',
      perMonth: '/month',
      perYear: '/year',
      save: 'Save',
      mostPopular: 'Most Popular',
      yearlySavings: 'Save $189 (16% off)',
      monthlyFeatures: ['Access full case details', 'View client contact information', 'Respond to unlimited cases', 'Priority listing in directory', 'Cancel anytime'],
      yearlyFeatures: ['All monthly features included', 'Access full case details', 'View client contact information', 'Respond to unlimited cases', 'Priority listing in directory'],
      selectPlan: 'Select Plan',
      selected: 'Selected',
      completeYourSubscription: 'Complete Your Subscription',
      mockPaymentNotice: 'Mock Payment: This is a simulated payment system. Click the button below to activate your subscription instantly.',
      selectedPlanInfo: 'Selected plan:',
      subscribeNow: 'Subscribe Now',
      completePayment: 'Complete Payment',
      selectedPlan: 'Selected Plan',
      paymentDetails: 'Payment Details',
      mockPayment: 'This is a mock payment for demonstration purposes.',
      cardNumber: 'Card Number',
      expiryDate: 'Expiry Date',
      cvv: 'CVV',
      completePurchase: 'Complete Purchase',
      processing: 'Processing...',
      successTitle: 'Subscription Activated!',
      successDesc: 'You now have full access to all case details and client information.',
      redirecting: 'Redirecting to dashboard...',
      year: 'year',
      month: 'month',
    },
    // Footer
    footer: {
      description: 'Connecting clients with qualified attorneys.',
      forClients: 'For Clients',
      findAttorney: 'Find an Attorney',
      submitCase: 'Submit a Case',
      getAdvice: 'Get Legal Advice',
      forAttorneys: 'For Attorneys',
      browseCases: 'Browse Cases',
      subscription: 'Subscription Plans',
      createProfile: 'Create Profile',
      copyright: '© 2026 AdvokatTOP. All rights reserved.',
      attorney: 'I am an attorney',
      privacy: 'Privacy Policy',
      terms: 'Terms of Service',
      deleteAccount: 'Delete Account',
    },
    survey: {
      question: 'Did you find an attorney?',
      yes: 'Yes',
      no: 'No',
      thanks: 'Thank you for your feedback!',
    },
    articles: {
      article1Title: 'How to File for Divorce in Uzbekistan',
      article2Title: "Tenant Rights: What You Need to Know",
      article3Title: 'Labor Disputes: How to Protect Your Rights',
      article4Title: 'Registering a Business in Uzbekistan',
      article5Title: 'Inheritance: Rights and Procedure',
      article6Title: 'Consumer Protection: How to Get a Refund',
    },
    // Common
    common: {
      loading: 'Loading...',
      error: 'Error',
      success: 'Success',
      save: 'Save',
      cancel: 'Cancel',
      submit: 'Submit',
      back: 'Back',
      next: 'Next',
      yes: 'Yes',
      no: 'No',
    },
    errors: {
      loginFailed: 'Login failed',
      googleLoginFailed: 'Google login failed',
      registrationFailed: 'Registration failed',
      attorneyNotFound: 'Attorney not found',
      subscriptionFailed: 'Subscription failed',
      userAlreadyExists: 'This email is already registered',
      invalidCredentials: 'Incorrect email/phone or password',
      serverError: 'Server error. Please try again',
      emailRequired: 'Please enter your email or phone',
      passwordRequired: 'Password is required',
      passwordTooShort: 'Password must be at least 6 characters',
      fullNameRequiredError: 'Please enter your name and surname',
      phoneRequiredError: 'Phone number is required',
      invalidScore: 'Invalid score',
      alreadyRated: 'You have already rated this attorney',
      profileNotFound: 'Profile not found',
      accessDenied: 'Access denied',
      fillAllFields: 'Please fill in all fields',
      writeQuestion: 'Please write your question',
      genericError: 'An error occurred. Please try again',
      describeSituation: 'Please describe your situation',
      enterPhone: 'Please enter your phone number',
      sendError: 'Failed to send. Please try again',
      requestsLoadError: 'Failed to load requests',
    },
  },
  ru: {
    // Navbar
    nav: {
      logo: 'AdvokatTop',
      home: 'Главная',
      attorneys: 'Адвокаты',
      articles: 'Статьи',
      findAttorney: 'Найти адвоката',
      login: 'Войти',
      register: 'Регистрация',
      dashboard: 'Панель управления',
      submitCase: 'Подать дело',
      getAdvice: 'Получить совет',
      browseCases: 'Просмотр дел',
      subscription: 'Подписка',
      logout: 'Выйти',
      lawyersByLocation: 'Адвокаты по регионам',
      lawyersByArea: 'Адвокаты по специализации',
      legalTopics: 'Правовые темы и Q&A',
    },
    regions: {
      tashkent_city: 'Ташкент (город)',
      tashkent: 'Ташкентская область',
      andijan: 'Андижанская область',
      fergana: 'Ферганская область',
      namangan: 'Наманганская область',
      samarkand: 'Самаркандская область',
      bukhara: 'Бухарская область',
      navoi: 'Навоийская область',
      kashkadarya: 'Кашкадарьинская область',
      surkhandarya: 'Сурхандарьинская область',
      jizzakh: 'Джизакская область',
      sirdarya: 'Сырдарьинская область',
      khorezm: 'Хорезмская область',
      karakalpakstan: 'Республика Каракалпакстан',
    },
    // Landing Page
    landing: {
      title: 'Найдите подходящую юридическую помощь',
      subtitle: 'Свяжитесь с опытными адвокатами или предложите свои юридические услуги',
      heroTitle: 'Юридическая проблема? Найдите адвоката рядом за 2 минуты',
      heroSubtitle: 'Просматривайте проверенных адвокатов, задайте вопрос или получите юридическую консультацию',
      searchPlaceholder: 'Опишите проблему или введите область права...',
      searchButton: 'Найти адвоката',
      amAttorneyBtn: 'Я адвокат',
      needHelp: 'Мне нужна юридическая помощь',
      needHelpDesc: 'Найдите квалифицированных адвокатов, задайте вопрос или получите юридическую консультацию',
      amAttorney: 'Я адвокат',
      amAttorneyDesc: 'Связывайтесь с клиентами, развивайте практику и помогайте нуждающимся',
      howItWorks: 'Как это работает',
      forClients: 'Для клиентов',
      forAttorneys: 'Для адвокатов',
      step1Title: 'Опишите проблему',
      step1Desc: 'Кратко расскажите о вашей ситуации — личные данные не требуются',
      step2Title: 'Выберите адвоката',
      step2Desc: 'Просмотрите анкеты проверенных адвокатов и их области практики',
      step3Title: 'Получите помощь',
      step3Desc: 'Свяжитесь с выбранным адвокатом и решите ваш юридический вопрос',
      step1Client: 'Создайте аккаунт и опишите ваши юридические потребности',
      step2Client: 'Просматривайте адвокатов или задайте вопрос на рассмотрение',
      step3Client: 'Свяжитесь с адвокатами и получите необходимую помощь',
      step1Attorney: 'Создайте профессиональный профиль',
      step2Attorney: 'Оформите подписку для доступа к делам и контактам клиентов',
      step3Attorney: 'Отвечайте на дела и расширяйте клиентскую базу',
      statsAttorneys: 'Адвокатов',
      statsCases: 'Дел решено',
      statsResponse: 'Среднее время ответа',
      statsResponseValue: '< 24 ч',
      quickFilters: 'Популярные:',
    },
    // Login/Register
    auth: {
      loginTitle: 'С возвращением',
      loginSubtitle: 'Войдите в свой аккаунт',
      registerTitle: 'Создать аккаунт',
      registerSubtitle: 'Присоединяйтесь к нашей юридической платформе',
      email: 'Электронная почта',
      emailOrPhone: 'Email или телефон',
      password: 'Пароль',
      confirmPassword: 'Подтвердите пароль',
      fullName: 'Полное имя',
      phone: 'Номер телефона',
      loginButton: 'Войти',
      registerButton: 'Создать аккаунт',
      noAccount: 'Нет аккаунта?',
      haveAccount: 'Уже есть аккаунт?',
      signUp: 'Зарегистрироваться',
      signIn: 'Войти',
      orContinueWith: 'Или продолжить с',
      iAmClient: 'Мне нужна юридическая помощь (Клиент)',
      iAmAttorney: 'Я адвокат',
      selectUserType: 'Я...',
      barNumber: 'Номер адвокатского удостоверения',
      location: 'Местоположение',
      practiceAreas: 'Области практики',
      bio: 'Профессиональная биография',
      hourlyRate: 'Почасовая ставка ($)',
    },
    // Dashboard
    dashboard: {
      welcome: 'Добро пожаловать',
      attorney: 'Адвокат',
      client: 'Клиент',
      manageProfile: 'Управляйте практикой и связывайтесь с клиентами',
      clientSubtitle: 'Управляйте делами и найдите юридическую помощь',
      subscriptionActive: 'Подписка активна',
      subscriptionRequired: 'Требуется подписка',
      planExpires: 'Ваш {plan} план истекает {date}',
      subscribeToAccess: 'Оформите подписку для доступа к полной информации о делах и контактам клиентов',
      subscribeNow: 'Оформить подписку',
      openCasesAvailable: 'Доступно открытых дел',
      yourHourlyRate: 'Ваша почасовая ставка',
      practiceAreasCount: 'Области практики',
      yourProfile: 'Ваш профиль',
      barNumber: 'Номер удостоверения',
      location: 'Местоположение',
      phoneLabel: 'Телефон',
      hourlyRateLabel: 'Почасовая ставка',
      notSet: 'Не указано',
      myCases: 'Мои дела (с ответами)',
      noRespondedCases: 'Вы ещё не ответили ни на одно дело.',
      browseAvailableCases: 'Просмотреть доступные дела',
      recentOpenCases: 'Недавние открытые дела',
      noOpenCases: 'На данный момент нет открытых дел.',
      viewAllCases: 'Смотреть все дела',
      subscribeToViewDetails: 'Подпишитесь для просмотра деталей',
      myCasesClient: 'Мои дела',
      noCasesYet: 'Вы ещё не подали ни одного дела.',
      submitYourFirst: 'Подать первое дело',
      adviceRequests: 'Запросы на консультацию',
      noAdviceRequests: 'Пока нет запросов на консультацию.',
      requestAdvice: 'Запросить юридическую консультацию',
    },
    // Cases
    cases: {
      title: 'Просмотр дел',
      subtitle: 'Найдите клиентов, ищущих юридическое представительство',
      subscribeBanner: 'Оформите подписку для просмотра полной информации о делах и контактов клиентов',
      practiceArea: 'Область практики',
      allAreas: 'Все области',
      status: 'Статус',
      allStatuses: 'Все статусы',
      open: 'Открыто',
      inProgress: 'В работе',
      closed: 'Закрыто',
      loading: 'Загрузка дел...',
      noCasesFound: 'Дела не найдены',
      adjustFilters: 'Попробуйте изменить фильтры',
      posted: 'Опубликовано',
      client: 'Клиент',
      subscribeToView: 'Подпишитесь для просмотра описания дела и данных клиента',
      loginToView: 'Войдите как подписанный адвокат для просмотра деталей',
      backToCases: 'Назад к делам',
      backToDashboard: 'Назад к панели управления',
      description: 'Описание',
      clientInformation: 'Информация о клиенте',
      name: 'Имя',
      emailLabel: 'Email',
      phoneLabel: 'Телефон',
      locationLabel: 'Местоположение',
      subscriptionRequiredTitle: 'Требуется подписка',
      subscriptionRequiredDesc: 'Оформите подписку для просмотра полного описания дела и контактной информации клиента.',
      responses: 'Ответы',
      noResponsesYet: 'Пока нет ответов.',
      sendResponse: 'Отправить ответ',
      writeProposal: 'Напишите ваше предложение или ответ на это дело...',
      submitResponse: 'Отправить ответ',
      submitting: 'Отправка...',
      responseSuccess: 'Ответ успешно отправлен',
      responseUpdated: 'Ответ успешно обновлён',
      editResponse: 'Редактировать ответ',
      updateResponse: 'Обновить ответ',
      subscribeToRespond: 'Оформите подписку, чтобы отвечать на дела.',
    },
    practiceAreas: {
      divorce: 'Развод',
      alimony: 'Алименты',
      inheritance: 'Наследство',
      debt_collection: 'Взыскание долга',
      fraud: 'Мошенничество',
      ban_check: 'Проверка запрета',
      tax: 'Налоги',
      business: 'Предпринимательство',
      credit: 'Кредит',
      labor: 'Трудовые споры',
      traffic_accident: 'ДТП',
      traffic_fine: 'ГАИ/Штрафы',
      housing_compensation: 'Компенсация (снос жилья)',
      cadastre: 'Кадастр',
      hoa: 'ЖЭК',
      construction: 'Строительство',
      subsidy: 'Субсидия',
      corporate: 'Корпоратив',
      criminal: 'Уголовное дело',
      administrative: 'Административное дело',
      property_division: 'Раздел имущества',
      intellectual_property: 'Интеллектуальная собственность',
      consumer_rights: 'Права потребителей',
      other: 'Другое',
    },
    statuses: {
      open: 'Открыто',
      in_progress: 'В работе',
      closed: 'Закрыто',
      paid: 'Оплачено',
      pending: 'Ожидает',
    },
    clientDashboard: {
      findAttorneyTitle: 'Найти адвоката',
      findAttorneyDesc: 'Просматривайте и ищите квалифицированных адвокатов в вашем регионе',
      submitCaseTitle: 'Подать дело',
      submitCaseDesc: 'Опубликуйте ваше юридическое дело и получите предложения от адвокатов',
      getLegalAdviceTitle: 'Получить юридическую консультацию',
      getLegalAdviceDesc: 'Запросите профессиональную юридическую консультацию по конкретному вопросу',
      yourProfile: 'Ваш профиль',
      yourCases: 'Ваши дела',
      adviceRequests: 'Запросы на консультацию',
      phone: 'Телефон',
      location: 'Местоположение',
      responses: 'ответов',
    },
    register: {
      iNeedHelp: 'Мне нужна помощь',
      imAnAttorney: 'Я адвокат',
      emailRequired: 'Email *',
      passwordRequired: 'Пароль *',
      forAttorneys: 'Для адвокатов',
      confirmPasswordRequired: 'Подтвердите пароль *',
      fullNameRequired: 'Полное имя *',
      lastName: 'Фамилия *',
      firstName: 'Имя *',
      middleName: 'Отчество *',
      phone: 'Телефон',
      location: 'Местоположение',
      locationPlaceholder: 'Город, штат',
      barNumberRequired: 'Номер удостоверения *',
      licenseDate: 'Дата выдачи лицензии',
      yearsOfExperience: 'Стаж работы адвокатом (лет)',
      attorneySignUpLink: 'Для регистрации в качестве адвоката нажмите здесь',
      hourlyRate: 'Почасовая ставка ($)',
      practiceAreas: 'Области практики',
      bio: 'Биография',
      bioPlaceholder: 'Расскажите потенциальным клиентам о вашем опыте...',
      creatingAccount: 'Создание аккаунта...',
      passwordsMismatch: 'Пароли не совпадают',
      passwordTooShort: 'Пароль должен быть не менее 6 символов',
      phoneInvalid: 'Введите номер телефона полностью',
    },
    // Submit Case
    submitCase: {
      title: 'Подать дело',
      subtitle: 'Опишите ваши юридические потребности и свяжитесь с квалифицированными адвокатами',
      caseTitle: 'Название дела',
      caseTitlePlaceholder: 'Краткое название вашего юридического вопроса',
      practiceArea: 'Область практики',
      selectArea: 'Выберите область практики',
      caseDescription: 'Описание дела',
      caseDescriptionPlaceholder: 'Пожалуйста, предоставьте детали вашей юридической ситуации. Чем больше информации вы предоставите, тем лучше адвокаты смогут понять ваши потребности.',
      submitButton: 'Подать дело',
      submittingButton: 'Отправка...',
      successMessage: 'Ваше дело успешно подано! Адвокаты смогут его рассмотреть и ответить.',
      goToDashboard: 'Перейти к панели управления',
    },
    // Request Advice
    requestAdvice: {
      title: 'Запросить юридическую консультацию',
      subtitle: 'Получите профессиональную юридическую помощь от квалифицированных адвокатов',
      subject: 'Тема',
      subjectPlaceholder: 'Краткая тема вашего юридического вопроса',
      description: 'Ваш вопрос',
      descriptionPlaceholder: 'Пожалуйста, подробно опишите ваш юридический вопрос...',
      paymentTitle: 'Информация об оплате',
      amount: 'Стоимость консультации',
      paymentNote: 'Это демонстрационная оплата.',
      cardNumber: 'Номер карты',
      expiryDate: 'Срок действия',
      cvv: 'CVV',
      submitPayment: 'Оплатить',
      processing: 'Обработка...',
      continueToPayment: 'Перейти к оплате',
      completePayment: 'Завершить оплату',
      amountLabel: 'Сумма:',
      mockPaymentInfo: 'Это тестовый платёж. Нажмите «Оплатить», чтобы симулировать успешную оплату.',
      payNow: 'Оплатить (тест)',
      paidService: 'Платная услуга:',
      paidServiceDesc: 'Это премиальная функция. Ваш запрос будет направлен нашей юридической группе. Для завершения отправки требуется оплата.',
      feeLabel: 'Единовременная оплата за юридическую консультацию',
      questionHint: 'Будьте как можно конкретнее',
      successTitle: 'Запрос успешно отправлен!',
      successDetails: 'Ваш запрос на консультацию отправлен. Email-уведомление доставлено.',
      redirecting: 'Перенаправление на панель управления...',
      successMessage: 'Ваш запрос на консультацию успешно отправлен! Адвокат ответит на вашу электронную почту в ближайшее время.',
      headline: 'Получите юридическую консультацию',
      headlineSub: 'Получите ответ от опытного адвоката в кратчайшие сроки',
      fullName: 'Полное имя',
      phone: 'Телефон',
      questionLabel: 'По какому вопросу нужна консультация?',
      questionPlaceholder: 'Подробно опишите вашу проблему...',
      priceLabel: 'Стоимость консультации',
      payBtn: 'Перейти к оплате',
      preparing: 'Подготовка...',
      registerAndPay: 'Зарегистрироваться и оплатить',
      choosePayment: 'Выберите способ оплаты',
      cancel: 'Отмена',
      securePayment: 'Платёж осуществляется по безопасному зашифрованному каналу',
      scanQr: 'Отсканируйте QR-код в приложении Payme',
      autoDetect: 'Платёж определяется автоматически...',
    },
    // Find Attorney
    findAttorney: {
      title: 'Найти адвоката',
      subtitle: 'Просмотрите каталог квалифицированных специалистов',
      search: 'Поиск',
      searchPlaceholder: 'Поиск по имени...',
      searchPlaceholderDetailed: 'Имя или ключевое слово...',
      practiceArea: 'Область практики',
      allAreas: 'Все области практики',
      locationLabel: 'Местоположение',
      locationPlaceholder: 'Город или область...',
      searchButton: 'Найти',
      loading: 'Загрузка адвокатов...',
      noAttorneysFound: 'Адвокаты не найдены',
      adjustSearch: 'Попробуйте изменить критерии поиска',
      perHour: '/час',
      viewProfile: 'Посмотреть профиль',
      noLocationSpecified: 'Местоположение не указано',
      more: 'ещё',
      experienceLabel: 'Стаж',
      months: 'мес.',
      years: 'лет',
      viewProfileBtn: 'Подробнее',
      callBtn: 'Позвонить',
      profileBtn: 'Профиль',
      messageBtn: 'Сообщение',
      ratingLabel: 'Рейтинг',
      ratingScore: 'балл',
      ratingReviews: 'отзывов',
      ratingInfoTitle: 'О рейтинге',
      ratingInfoBody: 'Рейтинг адвоката — это средняя оценка по 5-звёздной шкале, выставленная зарегистрированными клиентами на данной платформе. Каждый клиент может оценить адвоката один раз, выбрав от 1 до 5 звёзд.',
      ratingDisclaimer: 'Отказ от ответственности',
      ratingDisclaimerText: 'Пользователь выставляет оценку исходя из качества оказанных адвокатом услуг. В связи с этим рейтинг адвоката зависит от субъективных факторов. Мы не рекомендуем выбирать адвоката, опираясь исключительно на его рейтинг. Платформа AdvokatTop не участвует в формировании рейтинга адвоката и не гарантирует, что рейтинг отражает его квалификацию.',
    },
    // Attorney Profile
    attorneyProfile: {
      backToDirectory: 'Назад к каталогу',
      barNumber: 'Адвокатская лицензия',
      practiceAreas: 'Области практики',
      about: 'О себе',
      interestedInWorking: 'Хотите работать с {name}?',
      submitYourCase: 'Подать своё дело',
      requestAdviceBtn: 'Запросить консультацию',
      loginOrRegister: 'Войдите или зарегистрируйтесь как клиент, чтобы связаться с этим адвокатом.',
      selectForCase: 'Выбрать {name} для вашего дела',
      selectDescription: 'Вы собираетесь выбрать этого адвоката для ведения вашего дела. Адвокат получит уведомление, и дело будет отмечено как назначенное.',
      selectButton: 'Выбрать этого адвоката',
      selecting: 'Выбор...',
      selectSuccess: 'Адвокат успешно выбран! Перенаправление на панель управления...',
    },
    contact: {
      loading: 'Загрузка...',
      notFound: 'Адвокат не найден',
      sentTitle: 'Сообщение отправлено!',
      sentBody: 'Ваше сообщение успешно отправлено адвокату {name}. Вы получите ответ в ближайшее время.',
      backHome: 'На главную',
      title: 'Сообщение',
      requiredNote: 'Поля, отмеченные звёздочкой (*), обязательны для заполнения.',
      salutation: 'Уважаемый(ая) {name},',
      situationLabel: 'Опишите вашу ситуацию *',
      situationPlaceholder: 'Кратко опишите вашу юридическую ситуацию или вопрос...',
      phonePreferredLabel: 'Я предпочитаю, чтобы адвокат ответил мне по телефону (необязательно)',
      phoneLabel: 'Номер телефона *',
      preferredTimeLabel: 'Удобное время для звонка?',
      morning: 'Утром',
      afternoon: 'Днём',
      evening: 'Вечером',
      sending: 'Отправка...',
      sendBtn: 'Отправить сообщение',
      registerAndSendBtn: 'Зарегистрироваться и отправить',
      registerNote: 'Для отправки необходима регистрация. Ваши данные будут сохранены.',
    },
    messages: {
      loading: 'Загрузка...',
      myMessagesTitle: 'Мои сообщения',
      inboxTitle: 'Входящие сообщения',
      newSuffix: 'новых',
      noSentMessages: 'Вы ещё не отправили ни одного сообщения',
      noInboxMessages: 'У вас пока нет входящих сообщений',
      findAttorneyBtn: 'Найти адвоката',
      toLabel: 'Кому:',
      newReplyBadge: 'Новый ответ',
      anonymous: 'Аноним',
      phonePreferredClient: 'Предпочтителен звонок',
      phonePreferredAttorney: 'Просит ответить по телефону',
      you: 'Вы',
      writeReplyPlaceholder: 'Написать ответ...',
      sendShortcutHint: 'Ctrl+Enter для отправки',
      sending: 'Отправка...',
      replyBtn: 'Ответить',
      loadingThread: 'Загрузка переписки...',
    },
    // Subscription
    subscription: {
      title: 'Планы подписки',
      subtitle: 'Получите полный доступ к делам и связывайтесь с клиентами',
      currentPlan: 'Текущий план',
      currentSubscriptionActive: 'Подписка активна',
      yourPlanActiveUntil: 'Ваш план {plan} активен до',
      active: 'Активен',
      expired: 'Истёк',
      expiresOn: 'Истекает',
      choosePlan: 'Выберите план',
      monthly: 'Ежемесячный',
      yearly: 'Годовой',
      perMonth: '/месяц',
      perYear: '/год',
      save: 'Экономия',
      mostPopular: 'Самый популярный',
      yearlySavings: 'Экономия $189 (16%)',
      monthlyFeatures: ['Доступ к полным деталям дел', 'Просмотр контактов клиентов', 'Неограниченные ответы на дела', 'Приоритетное размещение в каталоге', 'Отмена в любое время'],
      yearlyFeatures: ['Все функции месячного плана', 'Доступ к полным деталям дел', 'Просмотр контактов клиентов', 'Неограниченные ответы на дела', 'Приоритетное размещение в каталоге'],
      selectPlan: 'Выбрать план',
      selected: 'Выбрано',
      completeYourSubscription: 'Оформить подписку',
      mockPaymentNotice: 'Тестовая оплата: Это симулированная платёжная система. Нажмите кнопку ниже, чтобы мгновенно активировать подписку.',
      selectedPlanInfo: 'Выбранный план:',
      subscribeNow: 'Подписаться',
      completePayment: 'Завершить оплату',
      selectedPlan: 'Выбранный план',
      paymentDetails: 'Данные оплаты',
      mockPayment: 'Это демонстрационная оплата.',
      cardNumber: 'Номер карты',
      expiryDate: 'Срок действия',
      cvv: 'CVV',
      completePurchase: 'Завершить покупку',
      processing: 'Обработка...',
      successTitle: 'Подписка активирована!',
      successDesc: 'Теперь у вас есть полный доступ ко всем деталям дел и информации о клиентах.',
      redirecting: 'Перенаправление на панель управления...',
      year: 'год',
      month: 'месяц',
    },
    // Footer
    footer: {
      description: 'Связываем клиентов с квалифицированными адвокатами.',
      forClients: 'Для клиентов',
      findAttorney: 'Найти адвоката',
      submitCase: 'Подать дело',
      getAdvice: 'Получить консультацию',
      forAttorneys: 'Для адвокатов',
      browseCases: 'Просмотр дел',
      subscription: 'Планы подписки',
      createProfile: 'Создать профиль',
      copyright: '© 2026 AdvokatTOP. Все права защищены.',
      attorney: 'Я адвокат',
      privacy: 'Политика конфиденциальности',
      terms: 'Условия обслуживания',
      deleteAccount: 'Удаление аккаунта',
      supportGroup: 'Группа поддержки',
    },
    supportGroup: {
      title: 'Группа поддержки',
      body: 'Уважаемый адвокат, здесь вы можете получить техническую поддержку, ответы на вопросы по использованию платформы, сообщить о возникших технических проблемах и оставить свои предложения.',
      joinBtn: 'Перейти в группу',
      dontShowAgain: 'Больше не показывать',
    },
    survey: {
      question: 'Удалось ли вам найти адвоката?',
      yes: 'Да',
      no: 'Нет',
      thanks: 'Спасибо за ответ!',
    },
    articles: {
      article1Title: 'Как оформить развод в Узбекистане',
      article2Title: 'Права арендатора: что нужно знать',
      article3Title: 'Трудовые споры: как защитить свои права',
      article4Title: 'Регистрация бизнеса в Узбекистане',
      article5Title: 'Наследство: права и процедура оформления',
      article6Title: 'Защита прав потребителей: как вернуть деньги',
    },
    // Common
    common: {
      loading: 'Загрузка...',
      error: 'Ошибка',
      success: 'Успешно',
      save: 'Сохранить',
      cancel: 'Отмена',
      submit: 'Отправить',
      back: 'Назад',
      next: 'Далее',
      yes: 'Да',
      no: 'Нет',
    },
    errors: {
      loginFailed: 'Ошибка входа',
      googleLoginFailed: 'Ошибка входа через Google',
      registrationFailed: 'Ошибка регистрации',
      attorneyNotFound: 'Адвокат не найден',
      subscriptionFailed: 'Ошибка подписки',
      userAlreadyExists: 'Этот email уже зарегистрирован',
      invalidCredentials: 'Неверный email/телефон или пароль',
      serverError: 'Ошибка сервера. Пожалуйста, попробуйте ещё раз',
      emailRequired: 'Введите email или телефон',
      passwordRequired: 'Введите пароль',
      passwordTooShort: 'Пароль должен содержать минимум 6 символов',
      fullNameRequiredError: 'Введите имя и фамилию',
      phoneRequiredError: 'Номер телефона обязателен',
      invalidScore: 'Неверный балл',
      alreadyRated: 'Вы уже оценили этого адвоката',
      profileNotFound: 'Профиль не найден',
      accessDenied: 'Доступ запрещён',
      fillAllFields: 'Заполните все поля',
      writeQuestion: 'Напишите ваш вопрос',
      genericError: 'Произошла ошибка. Пожалуйста, попробуйте ещё раз',
      describeSituation: 'Пожалуйста, опишите вашу ситуацию',
      enterPhone: 'Пожалуйста, введите номер телефона',
      sendError: 'Ошибка при отправке. Попробуйте ещё раз',
      requestsLoadError: 'Ошибка загрузки запросов',
    },
  },
  uz: {
    nav: {
      logo: 'AdvokatTop',
      home: 'Бош саҳифа',
      attorneys: 'Адвокатлар',
      articles: 'Мақолалар',
      findAttorney: 'Адвокат топиш',
      login: 'Кириш',
      register: 'Рўйхатдан ўтиш',
      dashboard: 'Бошқарув панели',
      submitCase: 'Иш топшириш',
      getAdvice: 'Маслаҳат олиш',
      browseCases: 'Ишларни кўриш',
      subscription: 'Обуна',
      logout: 'Чиқиш',
      lawyersByLocation: 'Ҳудуд бўйича адвокатлар',
      lawyersByArea: 'Мутахассислик бўйича адвокатлар',
      legalTopics: 'Ҳуқуқий мавзулардаги мақолалар',
    },
    regions: {
      tashkent_city: 'Тошкент (шаҳар)',
      tashkent: 'Тошкент вилояти',
      andijan: 'Андижон вилояти',
      fergana: 'Фарғона вилояти',
      namangan: 'Наманган вилояти',
      samarkand: 'Самарқанд вилояти',
      bukhara: 'Бухоро вилояти',
      navoi: 'Навоий вилояти',
      kashkadarya: 'Қашқадарё вилояти',
      surkhandarya: 'Сурхондарё вилояти',
      jizzakh: 'Жиззах вилояти',
      sirdarya: 'Сирдарё вилояти',
      khorezm: 'Хоразм вилояти',
      karakalpakstan: 'Қорақалпоғистон Республикаси',
    },
    landing: {
      title: 'Тўғри юридик ёрдамни топинг',
      subtitle: 'Тажрибали адвокатлар билан боғланинг ёки юридик хизматларингизни таклиф қилинг',
      heroTitle: 'Юридик муаммо? 2 дақиқада адвокат топинг',
      heroSubtitle: 'Тасдиқланган адвокатларни кўринг, ишингизни топширинг ёки тезкор юридик маслаҳат олинг',
      searchPlaceholder: 'Муаммоингизни тасвирланг ёки ҳуқуқ соҳасини киритинг...',
      searchButton: 'Адвокат топиш',
      amAttorneyBtn: 'Мен адвокатман',
      needHelp: 'Менга юридик ёрдам керак',
      needHelpDesc: 'Малакали адвокатларни топинг, ишингизни топширинг ёки юридик маслаҳат олинг',
      amAttorney: 'Мен адвокатман',
      amAttorneyDesc: 'Мижозлар билан боғланинг, амалиётингизни ривожлантиринг ва муҳтожларга ёрдам беринг',
      howItWorks: 'Бу қандай ишлайди',
      forClients: 'Мижозлар учун',
      forAttorneys: 'Адвокатлар учун',
      step1Title: 'Муаммоингизни тасвирланг',
      step1Desc: 'Юридик вазиятингиз ҳақида қисқача айтинг — шахсий маълумот талаб қилинмайди',
      step2Title: 'Адвокат танланг',
      step2Desc: 'Тасдиқланган адвокатлар профилларини кўриб, уларнинг амалиёт соҳаларини ўрганинг',
      step3Title: 'Юридик ёрдам олинг',
      step3Desc: 'Танлаган адвокатингиз билан боғланинг ва юридик масалангизни ҳал қилинг',
      step1Client: 'Аккаунт яратинг ва юридик эҳтиёжларингизни тасвирланг',
      step2Client: 'Адвокатларни кўринг ёки ишингизни кўриб чиқишга топширинг',
      step3Client: 'Адвокатлар билан боғланинг ва зарурий ёрдамни олинг',
      step1Attorney: 'Касбий профилингизни яратинг',
      step2Attorney: 'Мижоз ишлари ва контактларига кириш учун обуна бўлинг',
      step3Attorney: 'Ишларга жавоб беринг ва мижозлар базасини кенгайтиринг',
      statsAttorneys: 'Адвокатлар',
      statsCases: 'Ишлар ҳал қилинди',
      statsResponse: 'Ўрт. жавоб вақти',
      statsResponseValue: '< 24 соат',
      quickFilters: 'Машҳур:',
    },
    auth: {
      loginTitle: 'Хуш келибсиз',
      loginSubtitle: 'Аккаунтингизга киринг',
      registerTitle: 'Аккаунт яратиш',
      registerSubtitle: 'Ҳуқуқий платформамизга қўшилинг',
      email: 'Электрон почта',
      emailOrPhone: 'Email ёки телефон',
      password: 'Парол',
      confirmPassword: 'Паролни тасдиқланг',
      fullName: 'Тўлиқ исм',
      phone: 'Телефон рақами',
      loginButton: 'Кириш',
      registerButton: 'Аккаунт яратиш',
      noAccount: 'Аккаунтингиз йўқми?',
      haveAccount: 'Аккаунтингиз борми?',
      signUp: 'Рўйхатдан ўтиш',
      signIn: 'Кириш',
      orContinueWith: 'Ёки давом этинг',
      iAmClient: 'Менга юридик ёрдам керак (Мижоз)',
      iAmAttorney: 'Мен адвокатман',
      selectUserType: 'Мен...',
      barNumber: 'Адвокатлик гувоҳномаси рақами',
      location: 'Жойлашув',
      practiceAreas: 'Амалиёт соҳалари',
      bio: 'Касбий тарихча',
      hourlyRate: 'Соатлик тариф ($)',
    },
    dashboard: {
      welcome: 'Хуш келибсиз',
      attorney: 'Адвокат',
      client: 'Мижоз',
      manageProfile: 'Амалиётингизни бошқаринг ва мижозлар билан боғланинг',
      clientSubtitle: 'Ишларингизни бошқаринг ва юридик ёрдам топинг',
      subscriptionActive: 'Обуна фаол',
      subscriptionRequired: 'Обуна талаб қилинади',
      planExpires: '{plan} режангиз {date} да тугайди',
      subscribeToAccess: 'Ишлар ва мижоз контактларига тўлиқ кириш учун обуна бўлинг',
      subscribeNow: 'Обуна бўлиш',
      openCasesAvailable: 'Очиқ ишлар мавжуд',
      yourHourlyRate: 'Соатлик тарифингиз',
      practiceAreasCount: 'Амалиёт соҳалари',
      yourProfile: 'Профилингиз',
      barNumber: 'Гувоҳнома рақами',
      location: 'Жойлашув',
      phoneLabel: 'Телефон',
      hourlyRateLabel: 'Соатлик тариф',
      notSet: 'Кўрсатилмаган',
      myCases: 'Менинг ишларим (жавоб берилган)',
      noRespondedCases: 'Сиз ҳали ҳеч қайси ишга жавоб бермагансиз.',
      browseAvailableCases: 'Мавжуд ишларни кўриш',
      recentOpenCases: 'Сўнгги очиқ ишлар',
      noOpenCases: 'Ҳозирда очиқ ишлар мавжуд эмас.',
      viewAllCases: 'Барча ишларни кўриш',
      subscribeToViewDetails: 'Тафсилотларни кўриш учун обуна бўлинг',
      myCasesClient: 'Менинг ишларим',
      noCasesYet: 'Сиз ҳали ҳеч қайси иш топширмагансиз.',
      submitYourFirst: 'Биринчи ишингизни топширинг',
      adviceRequests: 'Маслаҳат сўровлари',
      noAdviceRequests: 'Ҳали маслаҳат сўровлари мавжуд эмас.',
      requestAdvice: 'Юридик маслаҳат сўраш',
    },
    cases: {
      title: 'Ишларни кўриш',
      subtitle: 'Юридик вакиллик изловчи мижозларни топинг',
      subscribeBanner: 'Ишларнинг тўлиқ тафсилоти ва мижоз контактларини кўриш учун обуна бўлинг',
      practiceArea: 'Амалиёт соҳаси',
      allAreas: 'Барча соҳалар',
      status: 'Ҳолат',
      allStatuses: 'Барча ҳолатлар',
      open: 'Очиқ',
      inProgress: 'Жараёнда',
      closed: 'Ёпиқ',
      loading: 'Ишлар юкланмоқда...',
      noCasesFound: 'Ишлар топилмади',
      adjustFilters: 'Фильтрларни ўзгартиришга ҳаракат қилинг',
      posted: 'Жойлаштирилди',
      client: 'Мижоз',
      subscribeToView: 'Иш тавсифи ва мижоз маълумотларини кўриш учун обуна бўлинг',
      loginToView: 'Тафсилотларни кўриш учун обунали адвокат сифатида киринг',
      backToCases: 'Ишларга қайтиш',
      backToDashboard: 'Бошқарув панелига қайтиш',
      description: 'Тавсиф',
      clientInformation: 'Мижоз маълумотлари',
      name: 'Исм',
      emailLabel: 'Электрон почта',
      phoneLabel: 'Телефон',
      locationLabel: 'Жойлашув',
      subscriptionRequiredTitle: 'Обуна талаб қилинади',
      subscriptionRequiredDesc: 'Ишнинг тўлиқ тавсифи ва мижоз контакт маълумотларини кўриш учун обуна бўлинг.',
      responses: 'Жавоблар',
      noResponsesYet: 'Ҳали жавоблар мавжуд эмас.',
      sendResponse: 'Жавоб юбориш',
      writeProposal: 'Таклифингизни ёки ушбу ишга жавобингизни ёзинг...',
      submitResponse: 'Жавобни юбориш',
      submitting: 'Юборилмоқда...',
      responseSuccess: 'Жавоб муваффақиятли юборилди',
      responseUpdated: 'Жавоб муваффақиятли янгиланди',
      editResponse: 'Жавобни таҳрирлаш',
      updateResponse: 'Жавобни янгилаш',
      subscribeToRespond: 'Ишларга жавоб бериш учун обуна бўлинг.',
    },
    practiceAreas: {
      divorce: 'Никоҳдан ажратиш',
      alimony: 'Алимент',
      inheritance: 'Меърос',
      debt_collection: 'Қарз ундириш',
      fraud: 'Фирибгарлик',
      ban_check: 'Тақиқ текшириш',
      tax: 'Солиқ',
      business: 'Тадбиркорлик билан боғлиқ',
      credit: 'Кредит',
      labor: 'Меҳнат низолари',
      traffic_accident: 'ДТП',
      traffic_fine: 'ГАИ/Штраф',
      housing_compensation: 'Компенсация (Уй-жой масаласи)',
      cadastre: 'Кадастр',
      hoa: 'ЖЭК',
      construction: 'Қурилиш',
      subsidy: 'Субсидия',
      corporate: 'Корпоратив',
      criminal: 'Жиноий иш',
      administrative: 'Маъмурий иш',
      property_division: 'Мол мулкни бўлиш',
      intellectual_property: 'Интеллектуал мулк',
      consumer_rights: 'Истеъмолчилар ҳуқуқлари',
      other: 'Бошқа',
    },
    statuses: {
      open: 'Очиқ',
      in_progress: 'Жараёнда',
      closed: 'Ёпиқ',
      paid: 'Тўланди',
      pending: 'Кутилмоқда',
    },
    clientDashboard: {
      findAttorneyTitle: 'Адвокат топиш',
      findAttorneyDesc: 'Ҳудудингиздаги малакали адвокатларни кўриб қидиринг',
      submitCaseTitle: 'Иш топшириш',
      submitCaseDesc: 'Юридик масалангизни эълон қилинг ва адвокатлардан таклифлар олинг',
      getLegalAdviceTitle: 'Юридик маслаҳат олиш',
      getLegalAdviceDesc: 'Муайян саволлар бўйича касбий юридик маслаҳат сўранг',
      yourProfile: 'Профилингиз',
      yourCases: 'Ишларингиз',
      adviceRequests: 'Маслаҳат сўровлари',
      phone: 'Телефон',
      location: 'Жойлашув',
      responses: 'жавоб',
    },
    register: {
      iNeedHelp: 'Менга ёрдам керак',
      imAnAttorney: 'Мен адвокатман',
      emailRequired: 'Электрон почта *',
      passwordRequired: 'Парол *',
      forAttorneys: 'Адвокатлар учун',
      confirmPasswordRequired: 'Паролни тасдиқланг *',
      fullNameRequired: 'Тўлиқ исм *',
      lastName: 'Фамилия *',
      firstName: 'Исм *',
      middleName: 'Шариф *',
      phone: 'Телефон',
      location: 'Жойлашув',
      locationPlaceholder: 'Шаҳар, вилоят',
      barNumberRequired: 'Гувоҳнома рақами *',
      licenseDate: 'Гувоҳнома берилган сана',
      yearsOfExperience: 'Адвокатлик стажи (йил)',
      attorneySignUpLink: 'Адвокатлар сифатида рўйҳатдан ўтиш учун шу ерга босинг',
      hourlyRate: 'Соатлик тариф ($)',
      practiceAreas: 'Амалиёт соҳалари',
      bio: 'Мен ҳақимда',
      bioPlaceholder: 'Потенциал мижозларга тажрибангиз ҳақида айтинг...',
      creatingAccount: 'Аккаунт яратилмоқда...',
      passwordsMismatch: 'Пароллар мос келмайди',
      passwordTooShort: 'Парол камида 6 та белгидан иборат бўлиши керак',
      phoneInvalid: 'Телефон рақамингизни тўлиқ киритинг',
    },
    submitCase: {
      title: 'Ишингизни топшириш',
      subtitle: 'Юридик эҳтиёжларингизни тасвирланг ва малакали адвокатлар билан боғланинг',
      caseTitle: 'Иш номи',
      caseTitlePlaceholder: 'Юридик масалангизни қисқача ифодаловчи ном',
      practiceArea: 'Амалиёт соҳаси',
      selectArea: 'Амалиёт соҳасини танланг',
      caseDescription: 'Иш тавсифи',
      caseDescriptionPlaceholder: 'Юридик вазиятингиз ҳақида тафсилотларни беринг. Қанча кўп маълумот берсангиз, адвокатлар эҳтиёжларингизни шунча яхши тушунади.',
      submitButton: 'Ишни топшириш',
      submittingButton: 'Юборилмоқда...',
      successMessage: 'Ишингиз муваффақиятли топширилди! Адвокатлар уни кўриб чиқиб жавоб бера олади.',
      goToDashboard: 'Бошқарув панелига ўтиш',
    },
    requestAdvice: {
      title: 'Юридик маслаҳат сўраш',
      subtitle: 'Малакали адвокатлардан касбий юридик йўриқнома олинг',
      subject: 'Мавзу',
      subjectPlaceholder: 'Юридик саволингизнинг қисқача мавзуси',
      description: 'Саволингиз',
      descriptionPlaceholder: 'Юридик саволингизни батафсил тасвирланг...',
      paymentTitle: 'Тўлов маълумотлари',
      amount: 'Маслаҳат сўров нархи',
      paymentNote: 'Бу намойиш учун тўлов.',
      cardNumber: 'Карта рақами',
      expiryDate: 'Амал қилиш муддати',
      cvv: 'CVV',
      submitPayment: 'Тўлаш',
      processing: 'Ишланмоқда...',
      continueToPayment: 'Тўловга ўтиш',
      completePayment: 'Тўловни якунлаш',
      amountLabel: 'Миқдор:',
      mockPaymentInfo: 'Бу синов тўлови. Муваффақиятли тўловни симуляция қилиш учун «Тўлаш»ни босинг.',
      payNow: 'Тўлаш (синов)',
      paidService: 'Пуллик хизмат:',
      paidServiceDesc: 'Бу мукаммал функция. Сўровингиз юридик гуруҳимизга юборилади. Топширишни якунлаш учун тўлов талаб қилинади.',
      feeLabel: 'Юридик маслаҳат сўрови учун бир марталик тўлов',
      questionHint: 'Имкон қадар аниқ бўлинг',
      successTitle: 'Сўров муваффақиятли юборилди!',
      successDetails: 'Маслаҳат сўровингиз юборилди. Электрон почта билдиришномаси етказилди.',
      redirecting: 'Бошқарув панелига йўналтирилмоқда...',
      successMessage: 'Маслаҳат сўровингиз муваффақиятли топширилди! Адвокат тез орада электрон почтангизга жавоб беради.',
      headline: 'Ҳуқуқий маслахат олинг',
      headlineSub: 'Тез фурсатда тажрибали адвокатдан жавоб оласиз',
      fullName: 'Тўлиқ исм',
      phone: 'Телефон',
      questionLabel: 'Қандай масала бўйича маслахат керак?',
      questionPlaceholder: 'Муаммоингизни батафсил тушунтиринг...',
      priceLabel: 'Маслаҳат нархи',
      payBtn: 'Тўловни амалга оширинг',
      preparing: 'Тайёрланмоқда...',
      registerAndPay: 'Рўйхатдан ўтиш ва тўловни қилиш',
      choosePayment: 'Тўлов усулини танланг',
      cancel: 'Бекор қилиш',
      securePayment: 'Тўлов хавфсиз шифрланган канал орқали амалга оширилади',
      scanQr: 'Payme иловасида QR кодни сканерланг',
      autoDetect: 'Тўлов автоматик аниқланади...',
    },
    findAttorney: {
      title: 'Адвокат топиш',
      subtitle: 'Малакали адвокатлар каталогини кўринг',
      search: 'Қидириш',
      searchPlaceholder: 'Исм бўйича қидириш...',
      searchPlaceholderDetailed: 'Исм ёки калит сўз...',
      practiceArea: 'Амалиёт соҳаси',
      allAreas: 'Барча амалиёт соҳалари',
      locationLabel: 'Ҳудуд',
      locationPlaceholder: 'Шаҳар ёки вилоят...',
      searchButton: 'Қидириш',
      loading: 'Адвокатлар юкланмоқда...',
      noAttorneysFound: 'Адвокатлар топилмади',
      adjustSearch: 'Қидирув мезонларини ўзгартиришга ҳаракат қилинг',
      perHour: '/соат',
      viewProfile: 'Профилни кўриш',
      noLocationSpecified: 'Ҳудуд кўрсатилмаган',
      more: 'яна',
      experienceLabel: 'Тажриба',
      months: 'ой',
      years: 'йил',
      viewProfileBtn: 'Батафсил',
      callBtn: 'Қўнғироқ қилиш',
      profileBtn: 'Профиль',
      messageBtn: 'Хабар ёзиш',
      ratingLabel: 'Рейтинг',
      ratingScore: 'балл',
      ratingReviews: 'шарҳлар',
      ratingInfoTitle: 'Рейтинг ҳақида',
      ratingInfoBody: 'Адвокат рейтинги — мазкур платформада рўйхатдан ўтган мижозлар томонидан берилган 5 юлдузли баҳоларнинг ўртача кўрсаткичи. Ҳар бир мижоз адвокатга бир марта, 1 дан 5 гачалик юлдуз билан баҳо бера олади.',
      ratingDisclaimer: 'Масъулиятдан воз кечиш',
      ratingDisclaimerText: 'Фойдаланувчи ўзига адвокат томонидан кўрсатилган хизмат сифатидан келиб чиқиб баҳо беради. Шу сабаб, адвокат рейтинги субъектив факторларга боғлиқдир. Адвокат хизматини фақат рейтингига асосланиб сотиб олишни тавсия бермаймиз. AdvokatTop платформаси адвокат рейтинги шаклланишида иштирок этмайди ва рейтинг адвокат малакасини акс эттиришини кафолатламайди.',
    },
    attorneyProfile: {
      backToDirectory: 'Каталогга қайтиш',
      barNumber: 'Адвокатлик лицензияси',
      practiceAreas: 'Амалиёт соҳалари',
      about: 'Адвокат ҳақида',
      interestedInWorking: '{name} билан ишлашни хоҳлайсизми?',
      submitYourCase: 'Ишингизни топшириш',
      requestAdviceBtn: 'Маслаҳат сўраш',
      loginOrRegister: 'Ушбу адвокат билан боғланиш учун мижоз сифатида киринг ёки рўйхатдан ўтинг.',
      selectForCase: 'Ишингиз учун {name}ни танланг',
      selectDescription: 'Сиз ушбу адвокатни ишингизни олиб бориш учун танламоқчисиз. Адвокат хабардор қилинади ва иш тайинланган деб белгиланади.',
      selectButton: 'Ушбу адвокатни танлаш',
      selecting: 'Танланмоқда...',
      selectSuccess: 'Адвокат муваффақиятли танланди! Бошқарув панелига йўналтирилмоқда...',
    },
    contact: {
      loading: 'Юкланмоқда...',
      notFound: 'Адвокат топилмади',
      sentTitle: 'Хабар юборилди!',
      sentBody: 'Хабарингиз {name} адвокатга муваффақиятли юборилди. Тез орада жавоб оласиз.',
      backHome: 'Бош саҳифага',
      title: 'Хабар',
      requiredNote: 'Юлдузча (*) билан белгиланган майдонлар тўлдирилиши шарт.',
      salutation: 'Ҳурматли {name},',
      situationLabel: 'Вазиятингизни таърифланг *',
      situationPlaceholder: 'Ҳуқуқий вазиятингиз ёки саволингизни қисқача ёзинг...',
      phonePreferredLabel: 'Адвокат менга телефон орқали жавоб беришини хоҳлайман (ихтиёрий)',
      phoneLabel: 'Телефон рақами *',
      preferredTimeLabel: 'Қўнғироқ қилиш учун қулай вақт?',
      morning: 'Эрталаб',
      afternoon: 'Кундузи',
      evening: 'Кечқурун',
      sending: 'Юборилмоқда...',
      sendBtn: 'Хабар юбориш',
      registerAndSendBtn: 'Рўйхатдан ўтиш ва юбориш',
      registerNote: 'Юбориш учун рўйхатдан ўтиш талаб қилинади. Маълумотларингиз сақланади.',
    },
    messages: {
      loading: 'Юкланмоқда...',
      myMessagesTitle: 'Хабарларим',
      inboxTitle: 'Келган хабарлар',
      newSuffix: 'та янги',
      noSentMessages: 'Сиз ҳали бирорта ҳам хабар юбормагансиз',
      noInboxMessages: 'Сизда ҳали келган хабарлар йўқ',
      findAttorneyBtn: 'Адвокат қидириш',
      toLabel: 'Кимга:',
      newReplyBadge: 'Янги жавоб',
      anonymous: 'Номаълум',
      phonePreferredClient: 'Қўнғироқ афзал кўрилган',
      phonePreferredAttorney: 'Телефон орқали жавоб беришни сўрайди',
      you: 'Сиз',
      writeReplyPlaceholder: 'Жавоб ёзинг...',
      sendShortcutHint: 'Юбориш учун Ctrl+Enter',
      sending: 'Юборилмоқда...',
      replyBtn: 'Жавоб бериш',
      loadingThread: 'Ёзишмалар юкланмоқда...',
    },
    subscription: {
      title: 'Обуна режалари',
      subtitle: 'Ишларга тўлиқ кириш имкониятини олинг ва мижозлар билан боғланинг',
      currentPlan: 'Жорий режа',
      currentSubscriptionActive: 'Жорий обуна фаол',
      yourPlanActiveUntil: '{plan} режангиз фаол',
      active: 'Фаол',
      expired: 'Муддати тугаган',
      expiresOn: 'Тугаш санаси',
      choosePlan: 'Режангизни танланг',
      monthly: 'Ойлик',
      yearly: 'Йиллик',
      perMonth: '/ой',
      perYear: '/йил',
      save: 'Тежаш',
      mostPopular: 'Энг машҳур',
      yearlySavings: '$189 тежаш (16% чегирма)',
      monthlyFeatures: ['Ишларнинг тўлиқ тафсилотига кириш', 'Мижоз контакт маълумотларини кўриш', 'Чексиз ишларга жавоб бериш', 'Каталогда устунлик', 'Исталган вақтда бекор қилиш'],
      yearlyFeatures: ['Барча ойлик функциялар', 'Ишларнинг тўлиқ тафсилотига кириш', 'Мижоз контакт маълумотларини кўриш', 'Чексиз ишларга жавоб бериш', 'Каталогда устунлик'],
      selectPlan: 'Режани танлаш',
      selected: 'Танланди',
      completeYourSubscription: 'Обунани расмийлаштириш',
      mockPaymentNotice: 'Синов тўлов: Бу симуляция тўлов тизими. Обунани дарҳол фаоллаштириш учун қуйидаги тугмани босинг.',
      selectedPlanInfo: 'Танланган режа:',
      subscribeNow: 'Обуна бўлиш',
      completePayment: 'Тўловни якунлаш',
      selectedPlan: 'Танланган режа',
      paymentDetails: 'Тўлов маълумотлари',
      mockPayment: 'Бу намойиш учун тўлов.',
      cardNumber: 'Карта рақами',
      expiryDate: 'Амал қилиш муддати',
      cvv: 'CVV',
      completePurchase: 'Харидни якунлаш',
      processing: 'Ишланмоқда...',
      successTitle: 'Обуна фаоллаштирилди!',
      successDesc: 'Энди сизда барча иш тафсилотлари ва мижоз маълумотларига тўлиқ кириш имкони бор.',
      redirecting: 'Бошқарув панелига йўналтирилмоқда...',
      year: 'йил',
      month: 'ой',
    },
    footer: {
      description: 'Мижозларни малакали адвокатлар билан боғлаймиз.',
      forClients: 'Мижозлар учун',
      findAttorney: 'Адвокат топиш',
      submitCase: 'Иш топшириш',
      getAdvice: 'Юридик маслаҳат олиш',
      forAttorneys: 'Адвокатлар учун',
      browseCases: 'Ишларни кўриш',
      subscription: 'Обуна режалари',
      createProfile: 'Профил яратиш',
      copyright: '© 2026 AdvokatTOP. Барча ҳуқуқлар ҳимояланган.',
      attorney: 'Мен адвокатман',
      privacy: 'Махфийлик сиёсати',
      terms: 'Ҳизмат кўрсатиш шартлари',
      deleteAccount: 'Ҳисобни ўчириш',
      supportGroup: 'Қўллаб-қувватлаш гуруҳи',
    },
    supportGroup: {
      title: 'Қўллаб-қувватлаш гуруҳи',
      body: 'Ҳурматли адвокат, техник қўллаб-қувватлаш, платформадан фойдаланиш бўйича саволларингизга жавоб олиш, юзага келган техник муаммолар ҳақида хабар беришингиз ва ўз таклифларингизни қолдиришингиз мумкин.',
      joinBtn: 'Гуруҳга қўшилиш',
      dontShowAgain: 'Бошқа кўрсатилмасин',
    },
    survey: {
      question: 'Адвокат топа олдингизми?',
      yes: 'Ҳа',
      no: 'Йўқ',
      thanks: 'Жавобингиз учун раҳмат!',
    },
    articles: {
      article1Title: 'Ўзбекистонда ажрашиш тартиби',
      article2Title: 'Ижарачи ҳуқуқлари: нимани билиш керак',
      article3Title: 'Меҳнат низолари: ҳуқуқларингизни қандай ҳимоя қилиш',
      article4Title: 'Ўзбекистонда бизнесни рўйхатдан ўтказиш',
      article5Title: 'Мерос: ҳуқуқлар ва расмийлаштириш тартиби',
      article6Title: 'Истеъмолчи ҳуқуқларини ҳимоя қилиш: пулни қандай қайтариш',
    },
    common: {
      loading: 'Юкланмоқда...',
      error: 'Хатолик',
      success: 'Муваффақиятли',
      save: 'Сақлаш',
      cancel: 'Бекор қилиш',
      submit: 'Юбориш',
      back: 'Орқага',
      next: 'Кейин',
      yes: 'Ҳа',
      no: 'Йўқ',
    },
    errors: {
      loginFailed: 'Киришда хатолик юз берди',
      googleLoginFailed: 'Google орқали киришда хатолик юз берди',
      registrationFailed: 'Рўйхатдан ўтишда хатолик юз берди',
      attorneyNotFound: 'Адвокат топилмади',
      subscriptionFailed: 'Обунада хатолик юз берди',
      userAlreadyExists: 'Бу электрон почта аллақачон рўйхатдан ўтган',
      invalidCredentials: 'Электрон почта/телефон ёки парол нотўғри',
      serverError: 'Сервер хатолиги. Илтимос, қайта уриниб кўринг',
      emailRequired: 'Электрон почта ёки телефон киритинг',
      passwordRequired: 'Паролни киритинг',
      passwordTooShort: 'Парол камида 6 та белгидан иборат бўлиши керак',
      fullNameRequiredError: 'Исм ва фамилиянгизни киритинг',
      phoneRequiredError: 'Телефон рақами мажбурий',
      invalidScore: 'Нотўғри балл',
      alreadyRated: 'Сиз бу адвокатга аллақачон баҳо берган эдингиз',
      profileNotFound: 'Профиль топилмади',
      accessDenied: 'Кириш тақиқланди',
      fillAllFields: 'Барча майдонларни тўлдиринг',
      writeQuestion: 'Саволингизни ёзинг',
      genericError: 'Хатолик юз берди. Илтимос, қайта уриниб кўринг',
      describeSituation: 'Вазиятингизни тасвирланг',
      enterPhone: 'Телефон рақамингизни киритинг',
      sendError: 'Юборишда хатолик. Қайта уриниб кўринг',
      requestsLoadError: 'Сўровларни юклашда хатолик юз берди',
    },
  },
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('language') || 'uz';
  });

  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];

    for (const k of keys) {
      if (value && value[k] !== undefined) {
        value = value[k];
      } else {
        // Fallback to Russian
        value = translations.ru;
        for (const fallbackKey of keys) {
          if (value && value[fallbackKey] !== undefined) {
            value = value[fallbackKey];
          } else {
            return key; // Return key if not found
          }
        }
        break;
      }
    }

    return value;
  };

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'ru' ? 'uz' : 'ru');
  };

  const value = {
    language,
    setLanguage,
    toggleLanguage,
    t,
    isRussian: language === 'ru',
    isUzbek: language === 'uz',
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
